import React, { useState, useEffect, useRef } from 'react';
import { AuthShell, TalkuSymbol } from './Login';

// Registro. Con el registro v2 activo (backend signup.py: datos del negocio + reCAPTCHA v3 + código por correo)
// muestra los 3 pasos; si aún no está activo (faltan remitente o llaves), muestra el formulario anterior.

const API = 'https://talku.online';
const TIPOS = ['Tienda online', 'Dropshipping', 'Servicios', 'Otro'];
const CANALES = ['WhatsApp', 'Instagram', 'Facebook', 'Tienda Shopify'];
const EQUIPO = ['Solo yo', '2–5', '6–15', 'Más de 15'];
const FUENTES = ['Instagram', 'Facebook', 'Google', 'Nexus', 'Recomendación', 'Otro'];

const passScore = (v) => { if (!v) return 0; let s = 0; if (v.length >= 8) s++; if (/[A-Z]/.test(v) && /[a-z]/.test(v)) s++; if (/\d/.test(v)) s++; if (/[^A-Za-z0-9]/.test(v) || v.length >= 12) s++; return Math.max(1, s); };

function loadRecaptcha(siteKey) {
  return new Promise((resolve) => {
    if (window.grecaptcha && window.grecaptcha.execute) return resolve();
    if (document.getElementById('rc-v3')) { const iv = setInterval(() => { if (window.grecaptcha && window.grecaptcha.execute) { clearInterval(iv); resolve(); } }, 150); return; }
    const s = document.createElement('script');
    s.id = 'rc-v3'; s.async = true; s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.onload = () => window.grecaptcha.ready(resolve);
    s.onerror = () => resolve();
    document.head.appendChild(s);
  });
}

const Chips = ({ options, value, onChange, multi }) => (
  <div className="chips">
    {options.map(o => {
      const on = multi ? value.includes(o) : value === o;
      return <button key={o} type="button" className={`chip${on ? ' on' : ''}`} onClick={() => onChange(multi ? (on ? value.filter(x => x !== o) : [...value, o]) : o)}>{o}</button>;
    })}
  </div>
);

function SignupV2({ siteKey, onLogin, onShowLogin }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ name: '', email: '', whatsapp: '', password: '', business_name: '', city: '', business_type: '', channels: [], team_size: '', source: '', accept_terms: false });
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cd, setCd] = useState(0);
  const [done, setDone] = useState(false);
  const [shake, setShake] = useState(false);
  const otpRef = useRef([]);
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v && v.target ? (v.target.type === 'checkbox' ? v.target.checked : v.target.value) : v }));

  useEffect(() => { loadRecaptcha(siteKey); }, [siteKey]);
  useEffect(() => { if (cd <= 0) return undefined; const t = setTimeout(() => setCd(c => c - 1), 1000); return () => clearTimeout(t); }, [cd]);

  const next0 = (e) => {
    e.preventDefault(); setError('');
    if (!/^3\d{9}$/.test(f.whatsapp.replace(/\D/g, ''))) return setError('Escribe un celular de 10 dígitos que empiece por 3');
    if (f.password.length < 8) return setError('La contraseña debe tener al menos 8 caracteres');
    setStep(1);
  };

  const start = async (e) => {
    e.preventDefault(); setError('');
    if (!f.business_type) return setError('Elige el tipo de negocio');
    if (!f.channels.length) return setError('Elige por dónde vendes');
    if (!f.team_size) return setError('Elige cuántas personas atienden');
    if (!f.accept_terms) return setError('Debes aceptar los Términos y la Política de tratamiento de datos');
    setLoading(true);
    try {
      await loadRecaptcha(siteKey);
      let token = '';
      try { token = await window.grecaptcha.execute(siteKey, { action: 'signup' }); } catch { token = ''; }
      const r = await fetch(`${API}/auth/signup/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...f, recaptcha_token: token }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setStep(2); setCd(45); setTimeout(() => otpRef.current[0]?.focus(), 300); }
      else setError(typeof d.detail === 'string' ? d.detail : 'No se pudo continuar. Revisa los datos.');
    } catch { setError('Error de conexión. Intenta de nuevo.'); }
    setLoading(false);
  };

  const verify = async (e) => {
    e.preventDefault(); setError('');
    const c = code.join('');
    if (c.length < 6) { setShake(true); setTimeout(() => setShake(false), 400); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/auth/signup/verify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: f.email, code: c }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setDone(true);
        localStorage.setItem('talku_token', d.token);
        setTimeout(() => onLogin({ id: d.id, email: d.email, name: d.name, role: d.role, token: d.token, tenant_id: d.tenant_id }), 1100);
      } else { setError(typeof d.detail === 'string' ? d.detail : 'Código incorrecto'); setShake(true); setTimeout(() => setShake(false), 400); }
    } catch { setError('Error de conexión. Intenta de nuevo.'); }
    setLoading(false);
  };

  const resend = async (e) => {
    e.preventDefault(); if (cd > 0) return; setError('');
    const r = await fetch(`${API}/auth/signup/resend`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: f.email }) }).catch(() => null);
    if (r && r.ok) setCd(45); else { const d = r ? await r.json().catch(() => ({})) : {}; setError(typeof d.detail === 'string' ? d.detail : 'No se pudo reenviar'); }
  };

  const onDigit = (i, v) => {
    const digits = v.replace(/\D/g, '');
    if (digits.length > 1) { const arr = digits.slice(0, 6).split(''); setCode(c => c.map((_, k) => arr[k] ?? '')); otpRef.current[Math.min(arr.length, 5)]?.focus(); return; }
    setCode(c => c.map((x, k) => (k === i ? digits : x)));
    if (digits && otpRef.current[i + 1]) otpRef.current[i + 1].focus();
  };

  const STEPS = ['Tu cuenta', 'Tu negocio', 'Verificación'];
  const score = passScore(f.password);
  return (
    <div className="fbox">
      <div className="f-logo"><TalkuSymbol/></div>
      <div className="rg-steps">
        {STEPS.map((s, i) => <React.Fragment key={s}>{i > 0 && <b/>}<span className={i === step ? 'on' : i < step ? 'done' : ''}><i>{i + 1}</i>{s}</span></React.Fragment>)}
      </div>

      {step === 0 && (
        <form className="rg-pane on" onSubmit={next0}>
          <h1 className="f-h">Crea tu cuenta en Talku</h1>
          <p className="f-sub">Empieza con tus datos de acceso</p>
          <div className="grp">
            <div><label className="lbl" htmlFor="r-name">Tu nombre</label><div className="fld"><input id="r-name" placeholder="Nombre y apellido" value={f.name} onChange={set('name')} required minLength={3} autoComplete="name"/></div></div>
            <div><label className="lbl" htmlFor="r-email">Correo</label><div className="fld"><input id="r-email" type="email" placeholder="correo@empresa.com" value={f.email} onChange={set('email')} required autoComplete="email"/></div></div>
            <div><label className="lbl" htmlFor="r-wa">WhatsApp</label><div className="fld"><span className="pref">🇨🇴 +57</span><input id="r-wa" inputMode="tel" placeholder="300 123 4567" value={f.whatsapp} onChange={set('whatsapp')} required autoComplete="tel-national"/></div></div>
            <div><label className="lbl" htmlFor="r-pass">Contraseña</label><div className="fld"><input id="r-pass" type="password" placeholder="Mínimo 8 caracteres" value={f.password} onChange={set('password')} required autoComplete="new-password"/></div><div className={`meter${score ? ' s' + score : ''}`}><i/><i/><i/><i/></div></div>
          </div>
          {error && <div className="lg-err">{error}</div>}
          <button className="btn" type="submit">Continuar</button>
          <p className="alt">¿Ya tienes cuenta? <button type="button" className="lnk" onClick={onShowLogin}>Inicia sesión</button></p>
        </form>
      )}

      {step === 1 && (
        <form className="rg-pane on" onSubmit={start}>
          <h1 className="f-h">Cuéntanos de tu negocio</h1>
          <p className="f-sub">Así preparamos Talku para ti</p>
          <div className="grp">
            <div className="two">
              <div><label className="lbl" htmlFor="r-biz">Nombre del negocio</label><div className="fld"><input id="r-biz" placeholder="Mi tienda" value={f.business_name} onChange={set('business_name')} required minLength={2} autoComplete="organization"/></div></div>
              <div><label className="lbl" htmlFor="r-city">Ciudad</label><div className="fld"><input id="r-city" placeholder="Bogotá" value={f.city} onChange={set('city')} required minLength={2} autoComplete="address-level2"/></div></div>
            </div>
            <div><span className="lbl">Tipo de negocio</span><Chips options={TIPOS} value={f.business_type} onChange={set('business_type')}/></div>
            <div><span className="lbl">¿Por dónde vendes? <em>puedes elegir varios</em></span><Chips options={CANALES} value={f.channels} onChange={set('channels')} multi/></div>
            <div><span className="lbl">¿Cuántas personas atienden?</span><Chips options={EQUIPO} value={f.team_size} onChange={set('team_size')}/></div>
            <div><label className="lbl" htmlFor="r-src">¿Cómo nos conociste? <em>opcional</em></label><div className="fld"><select id="r-src" value={f.source} onChange={set('source')}><option value="">Selecciona…</option>{FUENTES.map(o => <option key={o}>{o}</option>)}</select></div></div>
            <label className="chk"><input type="checkbox" checked={f.accept_terms} onChange={set('accept_terms')}/><span/>Acepto los <a className="lnk" href="/terminos" target="_blank" rel="noreferrer">Términos</a> y la <a className="lnk" href="/tratamiento-datos" target="_blank" rel="noreferrer">Política de tratamiento de datos</a></label>
          </div>
          {error && <div className="lg-err">{error}</div>}
          <div className="row2">
            <button className="btn ghost" type="button" onClick={() => { setError(''); setStep(0); }}>Atrás</button>
            <button className="btn" type="submit" disabled={loading}>{loading && <span className="spin"/>}{loading ? 'Enviando código…' : 'Crear cuenta'}</button>
          </div>
          <p className="rc">Protegido por reCAPTCHA · <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacidad</a> · <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Términos</a></p>
        </form>
      )}

      {step === 2 && (
        <form className="rg-pane on" onSubmit={verify}>
          <h1 className="f-h">Revisa tu correo</h1>
          <p className="f-sub">Enviamos un código de 6 dígitos a <b>{f.email}</b></p>
          <div className={`otp${shake ? ' err' : ''}`}>
            {code.map((d, i) => (
              <input key={i} ref={el => { otpRef.current[i] = el; }} value={d} inputMode="numeric" autoComplete={i === 0 ? 'one-time-code' : 'off'} aria-label={`Dígito ${i + 1}`}
                className={d ? 'filled' : undefined} onChange={e => onDigit(i, e.target.value)}
                onKeyDown={e => { if (e.key === 'Backspace' && !code[i] && i > 0) otpRef.current[i - 1]?.focus(); }}/>
            ))}
          </div>
          {error && <div className="lg-err">{error}</div>}
          {!done && <button className="btn" type="submit" disabled={loading}>{loading && <span className="spin"/>}{loading ? 'Verificando…' : 'Verificar y entrar'}</button>}
          {!done && <p className="alt">¿No te llegó? <button type="button" className="lnk" onClick={resend} disabled={cd > 0}>Reenviar código</button> {cd > 0 && <span className="cd">(en {cd} s)</span>}</p>}
          <div className={`done${done ? ' on' : ''}`}><span className="ok"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><b>¡Cuenta verificada!</b><span>Entrando a Talku…</span></div>
        </form>
      )}
    </div>
  );
}

export default function Register({ onLogin, onShowLogin }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    let off = false;
    fetch(`${API}/auth/signup/config`).then(r => (r.ok ? r.json() : { enabled: false })).then(d => { if (!off) setCfg(d); }).catch(() => { if (!off) setCfg({ enabled: false }); });
    return () => { off = true; };
  }, []);
  if (!cfg) return <AuthShell><div className="fbox"><div className="f-logo"><TalkuSymbol/></div></div></AuthShell>;
  if (!cfg.enabled || !cfg.recaptcha_site_key) return <LegacyRegister onLogin={onLogin} onShowLogin={onShowLogin}/>;
  return (
    <AuthShell>
      <style>{`/* Registro por pasos */
.fbox{width:min(400px,100%)}
.rg-steps{display:flex;align-items:center;gap:8px;margin:0 0 22px;font-size:12px;color:var(--soft);animation:rise .6s var(--ease) .05s both}
.rg-steps span{display:flex;align-items:center;gap:6px;white-space:nowrap;transition:color .3s}
.rg-steps i{width:20px;height:20px;border-radius:50%;border:1px solid #E5E7EB;display:flex;align-items:center;justify-content:center;font-style:normal;font-size:11px;transition:background .3s,color .3s,border-color .3s}
.rg-steps span.on{color:var(--ink)}.rg-steps span.on i{background:var(--ink);border-color:var(--ink);color:#fff}
.rg-steps span.done i{background:var(--lime);border-color:var(--lime);color:#111}
.rg-steps b{flex:1;height:1px;background:#E5E7EB;min-width:12px}
.rg-pane{display:none}.rg-pane.on{display:flex;flex-direction:column;animation:paneIn .45s var(--ease) both}
@keyframes paneIn{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:none}}
.lbl em{font-style:normal;color:var(--soft);font-size:11.5px;margin-left:4px}
.pref{font-size:13px;color:var(--muted);padding-right:9px;border-right:1px solid #EFEFEF;white-space:nowrap}
.fld select{flex:1;border:none;outline:none;background:transparent;font:inherit;font-size:13.5px;color:var(--ink);height:100%;cursor:pointer}
.two{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.chips{display:flex;flex-wrap:wrap;gap:6px}
.chip{height:32px;padding:0 11px;border:1px solid #E5E7EB;border-radius:6px;background:#fff;font:inherit;font-size:12.5px;color:#374151;cursor:pointer;transition:background .15s,border-color .15s,color .15s,transform .15s}
.chip:hover{border-color:#C9C9C9}.chip:active{transform:scale(.96)}
.chip.on{background:var(--ink);border-color:var(--ink);color:#fff}
.chk{display:flex;align-items:flex-start;gap:9px;font-size:12.5px;color:var(--muted);line-height:1.45;cursor:pointer}
.chk input{position:absolute;opacity:0}
.chk span{width:16px;height:16px;border:1px solid #D1D5DB;border-radius:4px;flex:none;margin-top:1px;transition:background .15s,border-color .15s;display:flex;align-items:center;justify-content:center}
.chk input:checked+span{background:var(--ink);border-color:var(--ink)}
.chk input:checked+span::after{content:'';width:8px;height:4px;border-left:2px solid #fff;border-bottom:2px solid #fff;transform:rotate(-45deg) translate(1px,-1px)}
.meter{display:flex;gap:4px;margin-top:6px}.meter i{flex:1;height:3px;border-radius:2px;background:#EFEFEF;transition:background .3s}
.meter.s1 i:nth-child(-n+1){background:#ef4444}.meter.s2 i:nth-child(-n+2){background:#f59e0b}.meter.s3 i:nth-child(-n+3){background:#84cc16}.meter.s4 i{background:#16a34a}
.row2{display:grid;grid-template-columns:110px 1fr;gap:8px;margin-top:8px}.row2 .btn{margin-top:0}
.btn.ghost{background:#fff;color:var(--ink);border:1px solid #E5E7EB}.btn.ghost:hover{background:#F7F7F7}
.rc{font-size:11px;color:var(--soft);text-align:center;margin-top:14px}.rc a{color:inherit}
.otp{display:flex;justify-content:center;gap:8px;margin:6px 0 18px}
.otp input:nth-child(3){margin-right:10px}
.otp input{width:44px;height:50px;flex:none;border:1px solid #E5E7EB;border-radius:6px;text-align:center;font:inherit;font-size:22px;color:var(--ink);outline:none;transition:border-color .15s,box-shadow .15s,transform .15s}
.otp input:focus{border-color:var(--ink);box-shadow:0 0 0 3px rgba(17,17,17,.06)}
.otp input.filled{animation:pop .25s cubic-bezier(.34,1.56,.64,1)}
.otp.err input{border-color:#ef4444;animation:shake .35s}
@keyframes shake{0%,100%{transform:none}25%{transform:translateX(-4px)}75%{transform:translateX(4px)}}
.cd{color:var(--soft);font-size:12px}
.done{display:none;flex-direction:column;align-items:center;gap:6px;margin-top:18px;text-align:center}
.done.on{display:flex;animation:rise .5s var(--ease) both}
.done .ok{width:46px;height:46px;border-radius:50%;background:#16a34a;display:flex;align-items:center;justify-content:center;animation:pop .5s cubic-bezier(.34,1.56,.64,1)}
.done b{font-size:15px;font-weight:500}.done span:last-child{font-size:12.5px;color:var(--muted)}
.form-side{overflow-y:auto}


        .lnk:disabled{opacity:.5;cursor:default}
        .grecaptcha-badge{visibility:hidden}
      `}</style>
      <SignupV2 siteKey={cfg.recaptcha_site_key} onLogin={onLogin} onShowLogin={onShowLogin}/>
    </AuthShell>
  );
}

// ── Formulario anterior (mientras el registro v2 no esté activo) ──

const EyeIcon = ({ off }) => off
  ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
  : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;

function LegacyRegister({ onLogin, onShowLogin }) {
  const [business, setBusiness] = useState('');
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Las contraseñas no coinciden'); return; }
    if (password.length < 8)  { setError('La contraseña debe tener al menos 8 caracteres'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://talku.online/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ business_name: business, name, email, password }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('talku_token', data.token);
        onLogin({ id: data.id, email: data.email, name: data.name, role: data.role, token: data.token, tenant_id: data.tenant_id });
      } else {
        setError(data.detail || 'Error al crear la cuenta');
      }
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    }
    setLoading(false);
  };

  return (
    <AuthShell>
      <style>{`
        .rg-two{display:grid;grid-template-columns:1fr 1fr;gap:10px}
        @media (max-width:420px){.rg-two{grid-template-columns:1fr}}
        .grp>div:nth-child(3){animation-delay:.3s}.grp>div:nth-child(4){animation-delay:.36s}
      `}</style>
      <form className="fbox" onSubmit={handleSubmit}>
        <div className="f-logo"><TalkuSymbol/></div>
        <h1 className="f-h">Crea tu cuenta en Talku</h1>
        <p className="f-sub">Configura tu negocio y empieza a atender a tus clientes</p>
        <div className="grp">
          <div>
            <label className="lbl" htmlFor="rg-business">Nombre del negocio</label>
            <div className="fld"><input id="rg-business" type="text" placeholder="Mi tienda" value={business} onChange={e => setBusiness(e.target.value)} required autoComplete="organization"/></div>
          </div>
          <div>
            <label className="lbl" htmlFor="rg-name">Tu nombre</label>
            <div className="fld"><input id="rg-name" type="text" placeholder="Nombre y apellido" value={name} onChange={e => setName(e.target.value)} required autoComplete="name"/></div>
          </div>
          <div>
            <label className="lbl" htmlFor="rg-email">Correo</label>
            <div className="fld"><input id="rg-email" type="email" placeholder="correo@empresa.com" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email"/></div>
          </div>
          <div className="rg-two">
            <div>
              <label className="lbl" htmlFor="rg-pass">Contraseña</label>
              <div className="fld">
                <input id="rg-pass" type={showPass ? 'text' : 'password'} placeholder="Mín. 8 caracteres" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password"/>
                <button type="button" className="eye" onClick={() => setShowPass(v => !v)} tabIndex={-1} aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}><EyeIcon off={showPass}/></button>
              </div>
            </div>
            <div>
              <label className="lbl" htmlFor="rg-confirm">Confirmar</label>
              <div className="fld"><input id="rg-confirm" type={showPass ? 'text' : 'password'} placeholder="Repítela" value={confirm} onChange={e => setConfirm(e.target.value)} required autoComplete="new-password"/></div>
            </div>
          </div>
        </div>
        {error && <div className="lg-err">{error}</div>}
        <button className="btn" type="submit" disabled={loading}>
          {loading && <span className="spin"/>}
          {loading ? 'Creando cuenta…' : 'Crear cuenta'}
        </button>
        <p className="alt">¿Ya tienes cuenta? <button type="button" className="lnk" onClick={onShowLogin}>Inicia sesión</button></p>
      </form>
    </AuthShell>
  );
}
